import { useToast } from '@shared/hooks/use-toast';
import { getErrorMessage } from '@shared/utils/error.utils';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteReception } from '../../hooks/useDeleteReception';
import { ReceptionListItem } from '../../models/Reception';

interface DeleteReceptionModalProps {
  reception: ReceptionListItem;
}

const DeleteReceptionModal = forwardRef<ModalCenterRef, DeleteReceptionModalProps>(({ reception }, ref) => {
  const { t } = useTranslation('receptionsModule');
  const { toast } = useToast();
  const deleteMutation = useDeleteReception();

  const handleCancel = () => {
    (ref as React.RefObject<ModalCenterRef>)?.current?.closeModal();
  };

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(reception);

      toast({
        title: t('modal.delete.success_title'),
        description: t('modal.delete.success_description'),
      });

      handleCancel();
    } catch (error: any) {
      toast({
        title: t('modal.delete.error_title'),
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    }
  };

  const isLoading = deleteMutation.isPending;

  return (
    <ModalCenter ref={ref} title={t('modal.delete.title')} type="default" width="default">
      <div className="space-y-6">
        {/* Icono de advertencia */}
        <div className="flex justify-center">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
            <FontAwesomeIcon icon={faExclamationTriangle} className="w-8 h-8 text-destructive" />
          </div>
        </div>

        {/* Mensaje de confirmación */}
        <div className="text-center space-y-2">
          <p className="text-foreground">
            {t('modal.delete.confirmation')} <span className="font-semibold">{reception.code}</span>?
          </p>
          <p className="text-sm text-muted-foreground">{t('modal.delete.warning')}</p>
        </div>

        {/* Información de la recepción */}
        <div className="p-4 bg-muted/50 rounded-lg space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('table.carrier')}:</span>
            <span className="text-foreground font-medium">{reception.carrier}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('table.total_qty')}:</span>
            <span className="text-foreground font-medium">{reception.total_qty}</span>
          </div>
        </div>

        {/* Botones */}
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={handleCancel} size="lg" disabled={isLoading}>
            {t('button.cancel')}
          </Button>
          <Button variant="destructive" onClick={handleDelete} size="lg" disabled={isLoading}>
            {isLoading ? t('button.deleting') : t('button.delete')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

DeleteReceptionModal.displayName = 'DeleteReceptionModal';

export default DeleteReceptionModal;
